'use client';

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type ClassVoucherSelectionMode = 'single' | 'multiple';

export interface ClassVoucher {
  id: string;
  name: string;
  description: string;
  price: number;
  durationLabel?: string;
  highlightLabel?: string;
  highlightTone?: 'primary' | 'danger' | 'neutral';
  normalPriceLabel?: string;
  discountPriceLabel?: string;
  discountBadgeLabel?: string;
  priceLabel?: string;
  subLabel?: string;
  noticeLabel?: string;
  benefitLabel?: string;
  requiresPromotionCode?: boolean;
}

interface ClassVoucherSelectionContextValue {
  selectionMode: ClassVoucherSelectionMode;
  selectedIds: string[];
  toggle: (id: string) => void;
  selectOnly: (id: string) => void;
  setSelectedIds: Dispatch<SetStateAction<string[]>>;
}

const ClassVoucherSelectionContext =
  createContext<ClassVoucherSelectionContextValue | null>(null);

interface ClassVoucherProviderProps {
  selectionMode?: ClassVoucherSelectionMode;
  initialSelectedIds?: string[];
  children: ReactNode;
}

export const ClassVoucherProvider = ({
  selectionMode = 'multiple',
  initialSelectedIds = [],
  children,
}: ClassVoucherProviderProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const isInitialMount = useRef(true);
  const prevInitialSelectedIds = useRef(initialSelectedIds);

  // initialSelectedIds가 실제로 변경되었을 때만 selectedIds를 동기화
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // 배열 내용이 실제로 변경되었는지 확인
    const hasChanged =
      prevInitialSelectedIds.current.length !== initialSelectedIds.length ||
      prevInitialSelectedIds.current.some(
        (id, index) => id !== initialSelectedIds[index]
      );

    if (hasChanged) {
      setSelectedIds(initialSelectedIds);
      prevInitialSelectedIds.current = initialSelectedIds;
    }
  }, [initialSelectedIds]);

  const updateSelection = useCallback(
    (updater: (prev: string[]) => string[]) => {
      setSelectedIds(updater);
    },
    []
  );

  const toggle = useCallback(
    (id: string) => {
      updateSelection(prev => {
        const nextSet = new Set(prev);
        if (selectionMode === 'single') {
          if (nextSet.has(id)) {
            nextSet.delete(id);
          } else {
            nextSet.clear();
            nextSet.add(id);
          }
        } else {
          if (nextSet.has(id)) {
            nextSet.delete(id);
          } else {
            nextSet.add(id);
          }
        }
        return Array.from(nextSet);
      });
    },
    [selectionMode, updateSelection]
  );

  const selectOnly = useCallback(
    (id: string) => {
      updateSelection(() => [id]);
    },
    [updateSelection]
  );

  const value = useMemo<ClassVoucherSelectionContextValue>(() => {
    return {
      selectionMode,
      selectedIds,
      toggle,
      selectOnly,
      setSelectedIds: next => {
        if (typeof next === 'function') {
          updateSelection(prev => (next as (prev: string[]) => string[])(prev));
        } else {
          updateSelection(() => next);
        }
      },
    };
  }, [selectionMode, selectedIds, toggle, selectOnly, updateSelection]);

  return (
    <ClassVoucherSelectionContext.Provider value={value}>
      {children}
    </ClassVoucherSelectionContext.Provider>
  );
};

export const useClassVoucherSelection = () => {
  const context = useContext(ClassVoucherSelectionContext);
  if (!context) {
    throw new Error(
      'useClassVoucherSelection must be used within ClassVoucherProvider'
    );
  }
  return context;
};
